import React from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import IconBookMark from '../WishlistPage/IconBookMark';

import css from './WishlistToggle.module.css';

/**
 * The bookmark ("heart") button that adds or removes a listing from the
 * current user's wishlist. Users who are not logged in are sent to the signup
 * page instead.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.currentUser - The current user, or null when logged out
 * @param {Object} props.currentListing - The listing shown on the page
 * @param {Function} props.onUpdateProfile - Saves the new wishlist on the user's profile
 * @returns {JSX.Element} the bookmark button
 */
const WishlistToggle = props => {
  const { rootClassName, className, currentUser, currentListing, onUpdateProfile } = props;

  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();

  const isLiked = () => {
    const likedListings = currentUser && currentUser.attributes.profile.publicData.wishlist;
    return likedListings && likedListings.includes(currentListing.id.uuid);
  };

  const handleClick = event => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();

    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}), state);
      return;
    }

    const likedListings = currentUser?.attributes?.profile?.publicData?.wishlist;
    let newListings = Array.isArray(likedListings) ? [...likedListings] : [];

    if (isLiked()) {
      newListings = newListings.filter(l => l !== currentListing.id.uuid);
    } else {
      newListings.push(currentListing.id.uuid);
    }

    if (typeof onUpdateProfile === 'function') {
      onUpdateProfile({
        publicData: {
          wishlist: newListings,
        },
      });
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes} onClick={handleClick}>
      <IconBookMark liked={isLiked()} />
    </div>
  );
};

export default WishlistToggle;
